import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Download, RefreshCw, Loader2, History, ChevronLeft, ChevronRight } from 'lucide-react';

const SCOPE_LABEL = {
  player_referral: 'Player → Player',
  staff_referral:  'Staff → Player',
};

export default function LREPayoutHistoryPage() {
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [days, setDays] = useState(30);
  const [scopeFilter, setScopeFilter] = useState('');
  const limit = 50;

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit, skip: page * limit, days });
      if (scopeFilter) params.set('scope', scopeFilter);
      const r = await api.get(`/api/admin/referrals/lre/payouts?${params}`);
      setPayouts(r.data.payouts || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayouts(); /* eslint-disable-next-line */ }, [page, days, scopeFilter]);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams({ days });
      if (scopeFilter) params.set('scope', scopeFilter);
      const r = await api.get(`/api/admin/referrals/lre/payouts/export.csv?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lre_payouts_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('CSV export failed');
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className="space-y-5 anim-fadeInUp" data-testid="lre-payouts-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <History className="w-5 h-5 text-[#D4AF37]" /> LRE Payout History
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={days}
            onChange={e => { setDays(parseInt(e.target.value)); setPage(0); }}
            className="bg-[#0B0E14] border border-white/10 rounded-md px-3 py-1.5 text-sm"
            data-testid="lre-days-filter"
          >
            <option value={1}>Today</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <select
            value={scopeFilter}
            onChange={e => { setScopeFilter(e.target.value); setPage(0); }}
            className="bg-[#0B0E14] border border-white/10 rounded-md px-3 py-1.5 text-sm"
            data-testid="lre-scope-filter"
          >
            <option value="">All scopes</option>
            <option value="player_referral">Player → Player</option>
            <option value="staff_referral">Staff → Player</option>
          </select>
          <button
            onClick={fetchPayouts}
            className="p-2 rounded-md hover:bg-white/5 anim-press"
            data-testid="lre-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={downloadCsv}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-bold bg-[#D4AF37] text-black anim-press"
            data-testid="lre-csv-btn"
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/5 overflow-x-auto" style={{ background: 'rgba(255,255,255,0.02)' }}>
        <table className="w-full text-sm" data-testid="lre-payouts-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
              <th className="text-left py-2 px-3">When</th>
              <th className="text-left py-2 px-3">Scope</th>
              <th className="text-left py-2 px-3">Referrer</th>
              <th className="text-left py-2 px-3">Referee</th>
              <th className="text-left py-2 px-3">Method</th>
              <th className="text-right py-2 px-3">Deposit $</th>
              <th className="text-right py-2 px-3">Pct</th>
              <th className="text-right py-2 px-3">Reward</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="8" className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-gray-500" /></td></tr>
            )}
            {!loading && payouts.length === 0 && (
              <tr><td colSpan="8" className="py-12 text-center text-gray-500" data-testid="lre-payouts-empty">No payouts in this window.</td></tr>
            )}
            {!loading && payouts.map(p => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`lre-payout-row-${p.id}`}>
                <td className="py-2 px-3 text-gray-400 text-[11px] tabular-nums">{new Date(p.created_at).toLocaleString()}</td>
                <td className="py-2 px-3">
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold"
                        style={{ background: p.scope === 'player_referral' ? 'rgba(34,197,94,0.18)' : 'rgba(212,175,55,0.18)',
                                 color: p.scope === 'player_referral' ? '#86efac' : '#F5D87A' }}>
                    {SCOPE_LABEL[p.scope] || p.scope}
                  </span>
                </td>
                <td className="py-2 px-3 text-white">{p.referrer_username}</td>
                <td className="py-2 px-3 text-gray-300">{p.referee_username}</td>
                <td className="py-2 px-3 text-gray-400 text-[11px]">{p.deposit_method}</td>
                <td className="py-2 px-3 text-right tabular-nums text-gray-300">${(p.deposit_amount_usd || 0).toFixed(2)}</td>
                <td className="py-2 px-3 text-right tabular-nums text-gray-400">{(p.commission_pct || 0).toFixed(1)}%</td>
                <td className="py-2 px-3 text-right tabular-nums font-bold"
                    style={{ color: p.reward_currency === 'cash' ? '#4ade80' : '#A5B4FC' }}>
                  {p.reward_currency === 'cash' ? '$' : ''}{(p.reward_amount || 0).toFixed(2)}{p.reward_currency === 'fp' ? ' FP' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && total > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>
            Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="p-1 rounded hover:bg-white/5 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <span className="tabular-nums">{page + 1} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="p-1 rounded hover:bg-white/5 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
